import { HoveredButton, Picture } from '@ui';
import icons from '@icons';
import { Link } from '@compat/router';
import { preparePriceFloatObject } from '@utils/floatNumbers';
import { observer } from 'mobx-react';
import { declension } from '@utils/declension';
import { zatochka, accum, trash } from '@images';
import BuyButton from '@components/BuyButton';
import useClipboard from '@customHooks/useClipboard';
import './index.scss';

const {
  trash2: { grey: trash2GreyOutlined, red: trash2RedOutlined }
} = icons.Outline;

const {
  gift: { red: giftRedFilled }
} = icons.Fill;

const { cart } = icons.threeD;

export const MiniCartHeader = observer(
  ({ clearCart, cartItemsCount, cartNumber }) => {
    const { isCopied, copyPromocode } = useClipboard({
      text: `${cartNumber}`,
      notificationText: 'Номер успешно скопирован!'
    });
    const onNumberClick = e => {
      e.stopPropagation();
      copyPromocode();
    };
    return (
      <div className='mini-cart-header'>
        <div className='mini-cart-header__title'>
          <span className='medium'>
            Корзина{' '}
            <span
              onClick={onNumberClick}
              className={`${
                isCopied ? 'bg-[var(--light-green)]' : 'bg-[var(--light-pink)]'
              } cursor-pointer inline-block px-0.5 rounded-md`}
            >
              № {cartNumber.toLocaleString()}
            </span>
            :
          </span>{' '}
          {cartItemsCount}{' '}
          {declension(cartItemsCount, ['товар', 'товара', 'товаров'])}{' '}
        </div>
        <div className='mini-cart-header__clear-btn' onClick={clearCart}>
          Очистить корзину
        </div>
      </div>
    );
  }
);

export const MiniCartEmptyCart = observer(({ hideMiniCart }) => (
  <div className='mini-cart-empty'>
    <Picture icon={cart} width='100' height='100' />
    <div className='mini-cart-empty__title'>Корзина пока пуста</div>
    <div className='mini-cart-empty__text'>
      Выбирайте понравившееся товары в нашем каталоге и добавляйте их в корзину
    </div>
    <Link className='mini-cart-empty__btn' to='/catalog' onClick={hideMiniCart}>
      В каталог
    </Link>
  </div>
));

export const MiniCartProducts = observer(
  ({ products, specialPrice, removeProduct, productsAmount }) => (
    <ul className='mini-cart-products'>
      {products?.map(item => {
        const specialPriceItem = specialPrice.find(
          i => i.productId === item.product.id
        );
        const priceFloat = preparePriceFloatObject(
          specialPriceItem ? specialPriceItem.value : item.product.price
        );
        return (
          <li
            key={`${item.id}-${item.product.id}`}
            className='mini-cart-products__item'
          >
            <Link
              to={`/product/${item.product.id}`}
              className='mini-cart-products__img-title'
            >
              <div className='mini-cart-products__img-wrapper'>
                <img
                  className='mini-cart-products__img'
                  src={item.product.mainImage}
                  alt={item.product.title}
                />
              </div>

              <div className='mini-cart-products__title'>
                {item.product.title}
              </div>
            </Link>
            <div className='mini-cart-products__controls'>
              {item.product.isAvailableForPurchase ? (
                <BuyButton
                  canBeBought
                  blockDeleteOnMinus
                  mini
                  isMiniCart
                  productId={item.product.id}
                  cartItemId={item.id}
                  productsAmountToShow={productsAmount[item.id]}
                />
              ) : (
                <div className='missing-product'>нет на складе</div>
              )}

              <div className='mini-cart-products__price-wrapper'>
                {!!item.product.oldPrice && (
                  <div className='mini-cart-products__old-price'>
                    {item.product.oldPrice} ₽
                  </div>
                )}
                <div
                  className={`mini-cart-products__price${
                    item.product?.oldPrice
                      ? ' mini-cart-products__price--red'
                      : ''
                  }`}
                >
                  {priceFloat?.wholePart}
                  {priceFloat?.floatPart && (
                    <span className='mini-cart-products__price__decimal'>
                      ,{priceFloat.floatPart}
                    </span>
                  )}
                  <span className='mini-cart-products__price__ruble'>
                    &nbsp;₽
                  </span>
                </div>
              </div>
              <HoveredButton
                onClick={() => removeProduct(item)}
                icon={trash2GreyOutlined.x3}
                iconHover={trash2RedOutlined.x3}
                width={'16'}
                height={'16'}
              />
            </div>
          </li>
        );
      })}
    </ul>
  )
);

export const MiniCartGifts = observer(({ gifts }) => (
  <ul className='mini-cart-gifts'>
    {gifts?.map(item => (
      <li className='mini-cart-gifts__item' key={item.id}>
        <div className='mini-cart-gifts__img-wrapper'>
          <img
            className='mini-cart-gifts__img'
            src={item.mainImage}
            alt={item.title}
          />
        </div>
        <Link to={`/product/${item.id}`} className='mini-cart-gifts__title'>
          {item.title}
        </Link>
        <div className='mini-cart-gifts__icon'>
          <Picture icon={giftRedFilled} height='24' width='24' />
        </div>
      </li>
    ))}
  </ul>
));

const getIcon = service => {
  if (service.service.id === 8 || service.service.id === 12) {
    return zatochka;
  }
  if (service.service.id === 3) {
    return accum;
  }
  return trash;
};

export const MiniCartServices = observer(
  ({ removeService, services, cartItemsCount }) => (
    <ul className='mini-cart-services'>
      {services?.map((service, index) => {
        const icon = getIcon(service);
        return (
          <li
            key={service.service.id}
            className={`mini-cart-services__item${
              cartItemsCount === 0 && index === 0
                ? ' mini-cart__service--no-border'
                : ''
            }`}
          >
            <div className='mini-cart-services__icon-title'>
              <Picture icon={icon} height='48' width='48' />
              <div className='mini-cart-services__title'>
                {service.service.title}
              </div>
            </div>
            <div className='mini-cart-services__controls'>
              {service?.service?.subscription_period && (
                <div className='mini-cart-services__period'>
                  {service.service.subscription_period}{' '}
                  {declension(service.service.subscription_period, [
                    'день',
                    'дня',
                    'дней'
                  ])}
                </div>
              )}
              <div className='mini-cart-services__price-wrapper'>
                <span className='mini-cart-services__price'>
                  {+service.amount * +service.price} ₽
                </span>
              </div>
              <HoveredButton
                onClick={() => removeService(service)}
                icon={trash2GreyOutlined.x3}
                iconHover={trash2RedOutlined.x3}
                width={'16'}
                height={'16'}
              />
            </div>
          </li>
        );
      })}
    </ul>
  )
);

export const MiniCartInfo = observer(
  ({ hideMiniCart, cartItemsCount, subtotal }) => {
    const totalPriceObj = preparePriceFloatObject(subtotal);
    return (
      <div className='mini-cart-info'>
        <div className='mini-cart-info__info-wrapper'>
          <div>
            <span className='medium'> Итого: {cartItemsCount} </span>{' '}
            {declension(cartItemsCount, ['товар', 'товара', 'товаров'])} на{' '}
            <span className='medium'>
              {totalPriceObj.wholePart}
              {totalPriceObj.floatPart && (
                <span className='mini-cart-info__decimal'>
                  ,{totalPriceObj?.floatPart}
                </span>
              )}{' '}
              <span className='mini-cart-info__ruble'>₽</span>{' '}
            </span>
          </div>
          <div className='mini-cart-info__disclaimer'>
            Стоимость без учета доставки
          </div>
        </div>
        <Link className='mini-cart-info__btn' to='/cart' onClick={hideMiniCart}>
          Оформить
        </Link>
      </div>
    );
  }
);
